import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { LogOut, Home, Bell, Search, LayoutDashboard, Users, Network, CalendarCheck, Clock, Receipt, CreditCard, Crosshair, Star, BookOpen, FileText, Megaphone, Settings, ShoppingCart, Truck, Package, Warehouse, Layers, Calculator, ScrollText, Scale, Repeat, BarChart3, Landmark, Target, Building2, Menu, X, LayoutGrid, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { logout } from "../../redux/actions/authActions";
import { ToastProvider } from "../../components/admin/ToastContext";
import { logoWhite } from "../../assets";
import EmployeeAvatar from "../../components/admin/EmployeeAvatar";
import AdminLanguage from "../../components/language/AdminLanguage";
import { get, put } from "../../services/lewooApi";
import LazyLoader from "../../components/loader/LazyLoader";

const SIDEBAR_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/admin-panel/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: null },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/admin-panel/employees", label: "Employee Directory", icon: Users, permission: "canViewEmployees" },
      { to: "/admin-panel/org", label: "Org Chart", icon: Network, permission: "canViewEmployees" },
    ],
  },
  {
    label: "Time & Attendance",
    items: [
      { to: "/admin-panel/leaves", label: "Leave Management", icon: CalendarCheck, permission: "canViewLeaves" },
      { to: "/admin-panel/attendance", label: "Attendance", icon: Clock, permission: "canViewAttendance" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/admin-panel/expenses", label: "Expense Claims", icon: Receipt, permission: "canViewExpenses" },
      { to: "/admin-panel/payslips", label: "Payslips", icon: CreditCard, permission: "canViewPayslips" },
    ],
  },
  {
    label: "Talent",
    items: [
      { to: "/admin-panel/recruitment", label: "Recruitment", icon: Crosshair, permission: "canViewRecruitment" },
      { to: "/admin-panel/appraisals", label: "Appraisals", icon: Star, permission: "canViewAppraisals" },
      { to: "/admin-panel/training", label: "Training", icon: BookOpen, permission: "canViewTraining" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/admin-panel/documents", label: "Documents", icon: FileText, permission: "canViewDocuments" },
      { to: "/admin-panel/announcements", label: "Announcements", icon: Megaphone, permission: "canViewAnnouncements" },
    ],
  },
  {
    label: "CRM",
    items: [
      { to: "/admin-panel/crm", label: "Pipeline", icon: Target, permission: "canViewCRM" },
      { to: "/admin-panel/crm/customers", label: "Customers", icon: Building2, permission: "canViewCRM" },
      { to: "/admin-panel/crm/dashboard", label: "CRM Dashboard", icon: BarChart3, permission: "canViewCRM" },
      { to: "/admin-panel/crm/settings", label: "CRM Settings", icon: Settings, permission: "canManageCRM" },
    ],
  },
  {
    label: "Sales",
    items: [
      { to: "/admin-panel/sales", label: "Sales Orders", icon: ShoppingCart, permission: "canViewSales" },
      { to: "/admin-panel/sales/invoices", label: "Sales Invoices", icon: FileText, permission: "canViewSalesInvoices" },
    ],
  },
  {
    label: "Purchases",
    items: [
      { to: "/admin-panel/purchases", label: "Purchase Orders", icon: Truck, permission: "canViewPurchases" },
      { to: "/admin-panel/vendor-bills", label: "Vendor Bills", icon: FileText, permission: "canViewVendorBills" },
    ],
  },
  {
    label: "Inventory",
    items: [
      { to: "/admin-panel/products", label: "Products", icon: Package, permission: "canViewProducts" },
      { to: "/admin-panel/stock", label: "Stock", icon: Layers, permission: "canViewStock" },
      { to: "/admin-panel/stock/moves", label: "Stock Moves", icon: Layers, permission: "canViewStockMoves" },
      { to: "/admin-panel/stock/warehouses", label: "Warehouses", icon: Warehouse, permission: "canViewWarehouses" },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/admin-panel/accounts", label: "Chart of Accounts", icon: Calculator, permission: "canViewAccounts" },
      { to: "/admin-panel/journal", label: "Journal", icon: ScrollText, permission: "canViewJournal" },
      { to: "/admin-panel/moves/account", label: "Account Moves", icon: FileText, permission: "canViewAccountMoves" },
      { to: "/admin-panel/ledger", label: "General Ledger", icon: Scale, permission: "canViewLedger" },
      { to: "/admin-panel/periods", label: "Account Periods", icon: CalendarCheck, permission: "canViewPeriods" },
    ],
  },
  {
    label: "Treasury",
    items: [
      { to: "/admin-panel/payments", label: "Payments", icon: Landmark, permission: "canViewPayments" },
      { to: "/admin-panel/reconciliation", label: "Reconciliation", icon: Repeat, permission: "canViewReconciliation" },
    ],
  },
  {
    label: "Reporting",
    items: [
      { to: "/admin-panel/reports", label: "Financial Reports", icon: BarChart3, permission: "canViewReports" },
      { to: "/admin-panel/aging", label: "Receivables & Payables", icon: CreditCard, permission: "canViewAging" },
    ],
  },
];

const SETTINGS_ITEM = { to: "/admin-panel/settings", label: "Settings", icon: Settings, permission: "isSettingsAdmin" };

const resolvePermission = (permissions, key) => {
  if (!key) return true;
  if (permissions?.isAdmin) return true;
  if (permissions?.[key]) return true;
  if (key === "canViewCRM") return !!permissions?.canViewCrm;
  if (key === "canManageCRM") return !!permissions?.canManageCrm;
  return false;
};

const AdminPanelLayout = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useSelector((state) => state.auth);
  const [employee, setEmployee] = useState(null);
  const [empLoaded, setEmpLoaded] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("adminSidebarCollapsed") === "1"; } catch { return false; }
  });

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("adminSidebarCollapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (permissions === null) return;
    if (!permissions?.isAdmin && !permissions?.hasPanelAccess) {
      navigate("/admin/dashboard", { replace: true });
    } else {
      setAccessChecked(true);
    }
  }, [permissions, navigate]);

  useEffect(() => {
    if (!user || !accessChecked) return;
    (async () => {
      try {
        const empRes = await get("/api/employees?limit=500");
        const allEmployees = empRes?.data || [];
        const currentEmp = allEmployees.find((e) => e.work_email === user?.email) || null;
        if (currentEmp) setEmployee(currentEmp);
      } catch {}
      setEmpLoaded(true);
    })();
  }, [user, accessChecked]);

  const filteredGroups = useMemo(() => {
    return SIDEBAR_GROUPS
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => resolvePermission(permissions, i.permission)),
      }))
      .filter((g) => g.items.length > 0 && resolvePermission(permissions, g.permission));
  }, [permissions]);

  const settingsVisible = resolvePermission(permissions, SETTINGS_ITEM.permission);

  const allItems = useMemo(() => {
    const items = filteredGroups.flatMap((g) => g.items);
    if (settingsVisible) items.push(SETTINGS_ITEM);
    return items;
  }, [filteredGroups, settingsVisible]);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const notifRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [waUnreadCount, setWaUnreadCount] = useState(0);
  const waPollRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const result = await get("/api/announcements?limit=50");
      const rows = result?.data || [];
      const unread = rows
        .filter((a) => !a.read)
        .map((a) => ({
          id: a.lewoo_uid ?? a.id,
          title: a.name || a.title,
          preview: a.body || a.description || "",
          date: a.date_start ? new Date(a.date_start).toLocaleDateString(intl.locale) : "",
        }));
      setNotifications(unread);
    } catch {}
  }, [intl]);

  const loadWaUnreadCount = useCallback(async () => {
    try {
      const result = await get("/whatsapp/inbox/unread-count");
      const count = Number(result?.data?.unread_count) || 0;
      setWaUnreadCount(count);
    } catch {}
  }, []);

  useEffect(() => {
    loadNotifications();
    loadWaUnreadCount();
    waPollRef.current = setInterval(() => {
      loadNotifications();
      loadWaUnreadCount();
    }, 30000);
    return () => {
      if (waPollRef.current) clearInterval(waPollRef.current);
    };
  }, [loadNotifications, loadWaUnreadCount, location.pathname]);
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allItems.filter((i) => i.label.toLowerCase().includes(q));
  }, [searchQuery, allItems]);

  const handleSearchSelect = (to) => {
    setSearchQuery("");
    setSearchOpen(false);
    navigate(to);
  };

  const handleNotifClick = async (ann) => {
    if (ann.id != null) {
      await put(`/api/announcements/${ann.id}/read`).catch(() => {});
    }
    setNotifications((prev) => prev.filter((n) => n.id !== ann.id));
    setNotifOpen(false);
    navigate("/admin-panel/announcements");
  };

  const handleWaNotifClick = async () => {
    try { await put("/whatsapp/inbox/read-all").catch(() => {}); } catch {}
    setWaUnreadCount(0);
    setNotifOpen(false);
    navigate("/admin-panel/settings");
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/admin/login");
  };

  const activeTo = useMemo(() => {
    const path = location.pathname;
    let best = null;
    for (const item of allItems) {
      const base = item.to.split("?")[0];
      if (path === base || path.startsWith(base + "/")) {
        if (!best || base.length > best.length) best = base;
      }
    }
    return best;
  }, [location.pathname, allItems]);

  const isActive = (to) => activeTo === to.split("?")[0];

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  if (permissions === null || !accessChecked) {
    return <LazyLoader />;
  }

  return (
    <div className="min-h-screen flex bg-slate-50 font-sans antialiased">
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside
        className={`fixed top-0 left-0 ess-drawer flex flex-col z-40 overflow-hidden transition-all duration-200 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } ${sidebarCollapsed ? "w-[72px]" : "w-64"}`}
        style={{ background: "#14532d" }}
      >
        <div className={`flex items-center gap-3 py-[18px] border-b border-white/10 flex-shrink-0 ${sidebarCollapsed ? "px-3 justify-center" : "px-4"}`}>
          <img src={logoWhite} alt="Africa Systems" className="w-8 h-8 object-contain flex-shrink-0" />
          {!sidebarCollapsed && (
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-extrabold text-white leading-tight">Admin Panel</h1>
              <p className="text-[10px] text-white/40 mt-px">Management Portal</p>
            </div>
          )}
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-white/60 hover:text-white transition-colors" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>

        <nav className={`no-scrollbar flex-1 min-h-0 overflow-y-auto py-2 ${sidebarCollapsed ? "px-1.5" : "px-2"}`}>
          {filteredGroups.map((group) => (
            <div key={group.label}>
              {!sidebarCollapsed && (
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 px-2.5 py-1 mt-2">
                  {group.label}
                </p>
              )}
              {sidebarCollapsed && <div className="h-2" />}
              {group.items.map((item) => {
                const active = isActive(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={item.label}
                    className={`flex items-center rounded-md text-sm font-medium transition-colors mb-[2px] ${
                      sidebarCollapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-[8px]"
                    } ${
                      active
                        ? "bg-emerald-500/20 text-emerald-200 font-semibold"
                        : "text-white/55 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <item.icon size={16} className="flex-shrink-0" />
                    {!sidebarCollapsed && <span className="flex-1 truncate">{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className={`py-2.5 border-t border-white/10 flex-shrink-0 ${sidebarCollapsed ? "px-1.5" : "px-2.5"}`}>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`hidden lg:flex items-center rounded-md text-sm font-medium text-white/55 hover:bg-white/10 hover:text-white transition-colors mb-1.5 w-full ${
              sidebarCollapsed ? "justify-center py-2.5" : "gap-2.5 px-2.5 py-[8px]"
            }`}
          >
            {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
          {settingsVisible && (
            <NavLink
              to={SETTINGS_ITEM.to}
              title={SETTINGS_ITEM.label}
              className={`flex items-center rounded-md text-sm font-medium transition-colors mb-1.5 ${
                sidebarCollapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-[8px]"
              } ${
                isActive(SETTINGS_ITEM.to)
                  ? "bg-emerald-500/20 text-emerald-200 font-semibold"
                  : "text-white/55 hover:bg-white/10 hover:text-white"
              }`}
            >
              <SETTINGS_ITEM.icon size={16} className="flex-shrink-0" />
              {!sidebarCollapsed && <span className="flex-1 truncate">{SETTINGS_ITEM.label}</span>}
            </NavLink>
          )}
          <div className={`flex items-center py-2 ${sidebarCollapsed ? "justify-center px-0" : "gap-2.5 px-2"}`}>
            <EmployeeAvatar employee={employee || { name: user?.username || user?.displayName || user?.email || "U" }} size={32} fontSize={13} />
            {!sidebarCollapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-white truncate">
                  {user?.username || "User"}
                </p>
                <p className="text-[11px] text-emerald-400/80 truncate">{employee?.job_title || ""}</p>
                <p className="text-[10px] text-white/40 truncate">{user?.email || ""}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className={`flex-1 min-w-0 min-h-screen overflow-x-hidden transition-[margin] duration-200 ${sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-64"}`}>
        <ToastProvider>
        <header
          className={`fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 gap-3 z-30 shadow-sm transition-[left] duration-200 ${
            sidebarCollapsed ? "lg:left-[72px]" : "lg:left-64"
          }`}
        >
          <button onClick={() => setMobileOpen(true)} className="lg:hidden h-9 w-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" aria-label="Open menu">
            <Menu size={20} />
          </button>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            className="hidden lg:inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label="Toggle sidebar"
          >
            {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <AdminLanguage />
            <button
              onClick={() => { setSearchOpen(true); setSearchQuery(""); }}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              title="Search"
              aria-label="Search"
            >
              <Search size={16} />
              <span className="hidden sm:inline text-xs font-medium">Search</span>
            </button>
            <div ref={notifRef} className="relative">
              <button
                onClick={() => {
                  if (!notifOpen) loadNotifications();
                  setNotifOpen(!notifOpen);
                }}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 relative transition-colors"
                title="Notifications"
              >
                <Bell size={16} />
                <span className="hidden sm:inline text-xs font-medium">Notifications</span>
                {notifications.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white">
                    {notifications.length + waUnreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-xl z-50 animate-scale-in max-h-96 overflow-y-auto">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
                  </div>
                  {notifications.length === 0 && waUnreadCount === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                      <span className="text-2xl block mb-2">🔔</span>
                      No unread notifications
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {waUnreadCount > 0 && (
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Nouveaux messages WhatsApp</p>
                          <button
                            onClick={handleWaNotifClick}
                            className="w-full text-left px-2 py-2.5 mt-1 hover:bg-emerald-50/50 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <span className="w-2 h-2 rounded-full bg-[#166534] flex-shrink-0 mt-1.5" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">Un client vous a répondu</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {waUnreadCount} nouveau message{waUnreadCount > 1 ? "s" : ""} à relancer
                                </p>
                              </div>
                              <span className="ml-auto text-[10px] text-[#166534] font-bold">Ouvrir</span>
                            </div>
                          </button>
                        </div>
                      )}
                      {notifications.map((ann) => (
                        <button
                          key={ann.id}
                          onClick={() => handleNotifClick(ann)}
                          className="w-full text-left px-4 py-3 hover:bg-emerald-50/50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{ann.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{ann.preview}</p>
                              <p className="text-[11px] text-gray-400 mt-1">{ann.date}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {notifications.length > 0 && (
                    <Link
                      to="/admin-panel/announcements"
                      onClick={() => setNotifOpen(false)}
                      className="block px-4 py-2.5 text-center text-sm font-medium text-emerald-600 hover:bg-emerald-50 border-t border-gray-100"
                    >
                      Voir toutes les annonces
                    </Link>
                  )}
                </div>
              )}
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-sm text-slate-500 hover:bg-slate-100 hover:text-emerald-600 transition-colors"
              title="Back to site"
            >
              <Home size={16} />
              <span className="hidden sm:inline text-xs font-medium">Back to site</span>
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-sm text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
              title="Sign out"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline text-xs font-medium">Sign out</span>
            </button>
          </div>
        </header>

        <main className="p-4 sm:p-6 pt-[72px] sm:pt-[72px] min-w-0 w-full overflow-x-hidden">
          <Outlet />
        </main>
        </ToastProvider>
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[10vh]">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in">
            <div className="flex items-center gap-3 px-4">
              <Search size={16} className="text-slate-400 flex-shrink-0" />
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search modules..."
                className="w-full py-3.5 text-sm bg-transparent outline-none placeholder:text-slate-400"
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                aria-label="Close search"
              >
                <X size={16} />
              </button>
            </div>
            <div className="border-t border-slate-100 max-h-80 overflow-y-auto py-1">
              {searchQuery.trim() && searchResults.length > 0 && searchResults.map((r) => (
                <button
                  key={r.to}
                  onClick={() => handleSearchSelect(r.to)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors text-left"
                >
                  <r.icon size={16} className="flex-shrink-0" />
                  <span className="flex-1 font-medium text-gray-700">{r.label}</span>
                </button>
              ))}
              {searchQuery.trim() && searchResults.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-400">No modules found</div>
              )}
              {!searchQuery.trim() && (
                <div className="px-4 py-6 text-center text-sm text-gray-400">Type to search modules...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelLayout;